// FilterSelect.js
// Drop-in replacement for a single-value MUI Select: the same options, but the
// user can type to narrow the list before picking one. With `freeText`, a typed
// value that is not in the list is kept as-is on Enter or blur.

import React from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';

const toOption = (option) => (typeof option === 'string' ? { value: option, label: option } : option);

const FilterSelect = ({
  label,
  value,
  options,
  onChange,
  required,
  error,
  disabled,
  loading,
  freeText,
  helperText,
  placeholder,
  margin = 'dense',
  size,
  sx,
}) => {
  const normalized = (options || []).map(toOption);
  const current = value ?? '';
  const selected = normalized.find((option) => option.value === current) || (freeText && current ? { value: current, label: current } : null);

  return (
    <Autocomplete
      options={normalized}
      value={selected}
      onChange={(_, option) => {
        if (typeof option === 'string') onChange(option.trim());
        else onChange(option ? option.value : '');
      }}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label ?? '')}
      isOptionEqualToValue={(option, chosen) => option.value === chosen.value}
      disabled={disabled}
      loading={loading}
      freeSolo={freeText}
      autoSelect={freeText}
      autoHighlight={!freeText}
      fullWidth
      size={size}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          margin={margin}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={placeholder || (freeText ? 'Type to filter or enter your own' : 'Type to filter')}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default FilterSelect;
